import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { mapResponseError } from '@mybusinessmate/api-client';
import type {
  CreateDeliverableInput,
  RevealHistory,
  RevealResult,
  StaffDeliverable,
  UpdateDeliverableInput,
  Vault,
} from '@mybusinessmate/schema';
import { directoryKey } from './directory-api';
import { api } from './api';

export const vaultKey = ['vault'] as const;

/** The account vault (Figma C03.4): logins, files and links, with no secret in it. */
export const vaultQuery = () =>
  queryOptions({
    queryKey: vaultKey,
    queryFn: async (): Promise<Vault> => {
      const res = await api.api.deliverables.$get();
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
  });

/**
 * Opens one login. The values are a mutation result, not a query, so they are never cached or written anywhere: they
 * live in the page's state until the countdown ends. A 401 with `REAUTH_REQUIRED` means the password must be
 * confirmed first.
 */
export function useRevealLogin() {
  return useMutation({
    gcTime: 0,
    mutationFn: async (id: string): Promise<RevealResult> => {
      const res = await api.api.deliverables[':id'].reveal.$post({ param: { id } });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
  });
}

/** A short-lived link to one vault file. */
export async function vaultFileUrl(id: string, fieldId: string): Promise<{ url: string; fileName: string }> {
  const res = await api.api.deliverables[':id'].files[':fieldId'].download.$get({ param: { id, fieldId } });
  if (!res.ok) throw await mapResponseError(res);
  return res.json();
}

// --- The team's side (Figma A05.11) -----------------------------------------------------------------------------------

/** Every query of one business's vault hangs off this key, so a save or a delete refreshes the list and the counts. */
export const staffVaultKey = (businessId: string) => ['vault', 'staff', businessId] as const;

/** The business's whole vault as the team sees it: client-hidden fields included, secrets masked. */
export const staffVaultQuery = (businessId: string) =>
  queryOptions({
    queryKey: staffVaultKey(businessId),
    queryFn: async (): Promise<{ items: StaffDeliverable[] }> => {
      const res = await api.api.deliverables.business[':businessId'].$get({ param: { businessId } });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
    retry: false,
  });

/** Who has opened a login, newest first (Figma A05.11c "Recent reveals"). Fetched when a login is opened. */
export const revealHistoryQuery = (id: string) =>
  queryOptions({
    queryKey: ['vault', 'reveals', id] as const,
    queryFn: async (): Promise<RevealHistory> => {
      const res = await api.api.deliverables[':id'].reveals.$get({ param: { id } });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
    // A view just recorded must show: never serve an old list.
    staleTime: 0,
  });

/** Adds an item (no `id`) or edits one (with `id`). A secret left out of an edit is kept as it is. */
export function useSaveDeliverable(businessId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (p: {
      id?: string;
      input: CreateDeliverableInput | UpdateDeliverableInput;
    }): Promise<StaffDeliverable> => {
      const res = p.id
        ? await api.api.deliverables[':id'].$patch({ param: { id: p.id }, json: p.input as UpdateDeliverableInput })
        : await api.api.deliverables.business[':businessId'].$post({
            param: { businessId },
            json: p.input as CreateDeliverableInput,
          });
      if (!res.ok) throw await mapResponseError(res);
      return (await res.json()) as StaffDeliverable;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: staffVaultKey(businessId) });
      // The business's tab shows how many items the vault holds.
      void queryClient.invalidateQueries({ queryKey: directoryKey });
    },
  });
}

export function useDeleteDeliverable(businessId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.api.deliverables[':id'].$delete({ param: { id } });
      if (!res.ok) throw await mapResponseError(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: staffVaultKey(businessId) });
      void queryClient.invalidateQueries({ queryKey: directoryKey });
    },
  });
}
